import React from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, ChartEvent, ActiveElement, Chart } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { AnalyticsGeoData } from '../../types';
import ErrorBoundary from '../ErrorBoundary';

ChartJS.register(ArcElement, Tooltip, Legend);

interface AnalyticsPieChartProps {
  geoData: AnalyticsGeoData[];
  selectedRegion: string | null;
  selectedCountry: string | null;
  setSelectedRegion: (region: string | null) => void;
  setSelectedCountry: (country: string | null) => void;
  metric: 'sales' | 'orderCount' | 'itemCount';
}

const AnalyticsPieChart: React.FC<AnalyticsPieChartProps> = ({
  geoData,
  selectedRegion,
  selectedCountry,
  setSelectedRegion,
  setSelectedCountry,
  metric,
}) => {
  const formatMetric = (value: number, metric: string) => {
    if (metric === 'sales') return `$${value.toFixed(2)}`;
    return value.toString();
  };

  const totalValue = geoData.reduce((sum, g) => sum + (g.value || 0), 0);

  const pieChartData = {
    labels: selectedCountry
      ? geoData.map((g) => g.state || 'No State Data Available')
      : selectedRegion
      ? geoData.map((g) => g.country?.name || 'Unknown Country')
      : geoData.map((g) => g.region || 'Unknown Region'),
    datasets: [
      {
        data: geoData.map((g) => g.value || 0),
        backgroundColor: [
          '#3B7DD8', // accent blue
          '#E07856', // terracotta
          '#4FA37C', // sage green
          '#E3B341', // golden
          '#8E6FC9', // soft violet
          '#56A6C9', // sky teal
          '#D8689B', // rose
          '#7A9E5F', // olive
          '#C98B56', // amber brown
          '#7B8794', // slate grey
          '#5F6FC9', // periwinkle
          '#A3B85C', // chartreuse
        ],
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverBorderColor: 'rgb(255, 255, 255)',
        hoverOffset: 5,
      },
    ],
  };

  const handlePieClick = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
    if (elements.length > 0) {
      const index = elements[0].index;
      if (!selectedRegion) {
        const region = geoData[index]?.region;
        if (region) setSelectedRegion(region);
      } else if (!selectedCountry) {
        const country = geoData[index]?.country?.code;
        if (country) setSelectedCountry(country);
      }
    }
  };

  const resetPieChart = () => {
    if (selectedCountry) {
      setSelectedCountry(null);
    } else if (selectedRegion) {
      setSelectedRegion(null);
    }
  };

  const title = selectedCountry
    ? `${metric === 'sales' ? 'Sales' : metric === 'orderCount' ? 'Orders' : 'Items'} by State`
    : selectedRegion
    ? `${metric === 'sales' ? 'Sales' : metric === 'orderCount' ? 'Orders' : 'Items'} by Country`
    : `${metric === 'sales' ? 'Sales' : metric === 'orderCount' ? 'Orders' : 'Items'} by Region`;

  return (
    <div className="analytics-card analytics-pie-card">
      <div className="analytics-card-header">
        <h3>{title}</h3>
        {(selectedRegion || selectedCountry) && (
          <button className="analytics-action-button" onClick={resetPieChart}>
            Back to {selectedCountry ? 'Countries' : 'Regions'}
          </button>
        )}
      </div>
      <ErrorBoundary>
        <div className="analytics-pie-container">
          {pieChartData.labels.length > 0 && geoData.some((g) => g.value > 0) ? (
            <Doughnut
              data={pieChartData}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      font: {
                        family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
                        size: 14,
                      },
                      color: '#1f2a44',
                      padding: 20,
                    },
                  },
                  tooltip: {
                    backgroundColor: '#ffffff',
                    titleColor: '#1f2a44',
                    bodyColor: '#1f2a44',
                    borderColor: '#e5e7eb',
                    borderWidth: 1,
                    cornerRadius: 8,
                    padding: 12,
                    callbacks: {
                      label: (context) => {
                        const value = context.raw as number;
                        const percentage = totalValue ? ((value / totalValue) * 100).toFixed(1) : '0';
                        return `${context.label}: ${formatMetric(value, metric)} (${percentage}%)`;
                      },
                    },
                  },
                },
                onClick: handlePieClick,
                hover: {
                  mode: 'nearest',
                  intersect: true,
                },
              }}
            />
          ) : (
            <p>No {selectedCountry ? 'state' : selectedRegion ? 'country' : 'region'} data available for the selected filters.</p>
          )}
        </div>
      </ErrorBoundary>
    </div>
  );
};

export default AnalyticsPieChart;